import type { ViewClient } from '../../api/viewClientApiService';
import { CLIENT_LABELS } from '../../constants/labels';

/**
 * Formats a currency code into a human-readable label.
 * PLN is mapped to "zł - Złoty polski (PLN)"; other codes are returned as-is.
 */
function formatCurrency(currency: string | null | undefined): string {
  if (!currency) {
    return '—';
  }
  if (currency.toUpperCase() === 'PLN') {
    return 'zł - Złoty polski (PLN)';
  }
  return currency;
}

/** Combines country code and phone number into a single display string. */
function formatPhone(
  countryCode: string | null | undefined,
  phone: string | null | undefined
): string {
  if (!phone) {
    return '—';
  }
  return countryCode ? `${countryCode} ${phone}` : phone;
}

/** Returns trimmed value or em-dash placeholder for null/empty strings. */
function fmt(value: string | null | undefined): string {
  return value?.trim() || '—';
}

/** Translates role codes to Polish display labels (CLIENT → "Klienci"). */
function formatRole(role: string | null | undefined): string {
  if (!role) {
    return '—';
  }
  if (role.toUpperCase() === 'CLIENT') {
    return 'Klienci';
  }
  return role;
}

/** Props for a single labelled read-only field with optional test IDs. */
interface InfoFieldProps {
  label: string;
  value: string;
  testId?: string;
  valueTestId?: string;
}

const InfoField = ({ label, value, testId, valueTestId }: InfoFieldProps) => (
  <div className="flex min-w-0 flex-col gap-2">
    <h2
      className="text-bodySmall font-normal text-on-surface-variant"
      data-testid={testId ?? label}
    >
      {label}
    </h2>
    <p
      className="min-w-0 break-words text-bodyLarge font-normal text-neutral-10"
      data-testid={valueTestId ?? `${testId ?? label}-value`}
    >
      {value}
    </p>
  </div>
);

/** Props for the ViewClientBasicInfo component. */
interface ViewClientBasicInfoProps {
  client: ViewClient | null;
}

/**
 * Displays the "Podstawowe informacje" tab of the View Client page.
 *
 * Renders two cards:
 * - **Podstawowe dane** — client ID, email, phone, currency, role
 * - **Adres** — address lines, city, state, zip code, country
 *
 * All fields fall back to an em-dash when the value is absent.
 */
const ViewClientBasicInfo = ({ client }: ViewClientBasicInfoProps) => {
  return (
    <div className="flex flex-col gap-6 m-[6px]">
      {/* Basic Details */}
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-titleDefault font-medium text-on-surface-variant"
          data-testid="view-client-basic-title"
        >
          {CLIENT_LABELS.VIEW_CLIENT_NAV_BASIC_INFO}
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
        >
          <InfoField
            label={CLIENT_LABELS.VIEW_CLIENT_FIELD_CLIENT_ID}
            value={fmt(client?.clientId)}
            testId="view-client-field-client-id"
            valueTestId="view-client-value-client-id"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_EMAIL}
            value={fmt(client?.email)}
            testId="view-client-field-email"
            valueTestId="view-client-value-email"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_PHONE}
            value={formatPhone(
              client?.contactInfo?.countryCode,
              client?.contactInfo?.phoneNumber
            )}
            testId="view-client-field-phone"
            valueTestId="view-client-value-phone"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_CURRENCY}
            value={formatCurrency(client?.currency)}
            testId="view-client-field-currency"
            valueTestId="view-client-value-currency"
          />
          <InfoField
            label={CLIENT_LABELS.VIEW_CLIENT_FIELD_ROLE}
            value={formatRole(client?.role)}
            testId="view-client-field-role"
            valueTestId="view-client-value-role"
          />
        </div>
      </div>

      {/* Location Details */}
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-titleDefault font-medium text-on-surface-variant"
          data-testid="view-client-location-title"
        >
          {CLIENT_LABELS.SECTION_ADDRESS}
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}
        >
          <InfoField
            label={CLIENT_LABELS.FIELD_ADDRESS1}
            value={fmt(client?.address?.addressLine1)}
            testId="view-client-field-address1"
            valueTestId="view-client-value-address1"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_ADDRESS2}
            value={fmt(client?.address?.addressLine2)}
            testId="view-client-field-address2"
            valueTestId="view-client-value-address2"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_CITY}
            value={fmt(client?.address?.city)}
            testId="view-client-field-city"
            valueTestId="view-client-value-city"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_STATE}
            value={fmt(client?.address?.state)}
            testId="view-client-field-state"
            valueTestId="view-client-value-state"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_ZIP}
            value={fmt(client?.address?.zipCode)}
            testId="view-client-field-zip"
            valueTestId="view-client-value-zip"
          />
          <InfoField
            label={CLIENT_LABELS.FIELD_COUNTRY}
            value={fmt(client?.address?.country)}
            testId="view-client-field-country"
            valueTestId="view-client-value-country"
          />
        </div>
      </div>
    </div>
  );
};

export default ViewClientBasicInfo;
